import { useState, useEffect, useMemo } from 'react'
import styles from './HolidayPage.module.css'

const TARGET_DATE = new Date('2026-04-04T00:00:00')

interface TimeRemaining {
    days: number
    hours: number
    minutes: number
    seconds: number
    totalSeconds: number
}

interface Activity {
    verb: string
    name: string
    durationSeconds: number
}

interface TimeExample {
    verb: string
    name: string
    count: number
    durationText: string
}

// Specific books with reading times (in seconds)
const BOOKS: Activity[] = [
    { verb: 'read', name: 'The Great Gatsby', durationSeconds: 10800 }, // 3 hours
    { verb: 'read', name: '1984', durationSeconds: 19800 }, // 5.5 hours
    { verb: 'read', name: 'To Kill a Mockingbird', durationSeconds: 18000 }, // 5 hours
    { verb: 'read', name: 'Pride and Prejudice', durationSeconds: 20880 }, // 5.8 hours
    { verb: 'read', name: 'Harry Potter and the Sorcerer\'s Stone', durationSeconds: 15120 }, // 4.2 hours
    { verb: 'read', name: 'The Catcher in the Rye', durationSeconds: 14400 }, // 4 hours
    { verb: 'read', name: 'Lord of the Flies', durationSeconds: 16200 }, // 4.5 hours
    { verb: 'read', name: 'Animal Farm', durationSeconds: 7200 }, // 2 hours
    { verb: 'read', name: 'The Hobbit', durationSeconds: 25200 }, // 7 hours
    { verb: 'read', name: 'Fahrenheit 451', durationSeconds: 12600 }, // 3.5 hours
    { verb: 'read', name: 'Brave New World', durationSeconds: 21600 }, // 6 hours
    { verb: 'read', name: 'The Lord of the Rings trilogy', durationSeconds: 59400 }, // 16.5 hours
    { verb: 'read', name: 'War and Peace', durationSeconds: 75600 }, // 21 hours
    { verb: 'read', name: 'Moby Dick', durationSeconds: 61200 }, // 17 hours
    { verb: 'read', name: 'The Brothers Karamazov', durationSeconds: 68400 }, // 19 hours
]

// Specific films with runtimes (in seconds)
const FILMS: Activity[] = [
    { verb: 'watch', name: 'Casablanca', durationSeconds: 6120 }, // 102 minutes
    { verb: 'watch', name: 'The Godfather', durationSeconds: 10500 }, // 175 minutes
    { verb: 'watch', name: 'The Shawshank Redemption', durationSeconds: 8520 }, // 142 minutes
    { verb: 'watch', name: 'Inception', durationSeconds: 8880 }, // 148 minutes
    { verb: 'watch', name: 'Pulp Fiction', durationSeconds: 9240 }, // 154 minutes
    { verb: 'watch', name: 'The Dark Knight', durationSeconds: 9120 }, // 152 minutes
    { verb: 'watch', name: 'Interstellar', durationSeconds: 10140 }, // 169 minutes
    { verb: 'watch', name: 'The Matrix', durationSeconds: 8160 }, // 136 minutes
    { verb: 'watch', name: 'Goodfellas', durationSeconds: 8760 }, // 146 minutes
    { verb: 'watch', name: 'Fight Club', durationSeconds: 8340 }, // 139 minutes
    { verb: 'watch', name: 'Forrest Gump', durationSeconds: 8520 }, // 142 minutes
    { verb: 'watch', name: 'The Lord of the Rings: The Fellowship of the Ring', durationSeconds: 10920 }, // 182 minutes
    { verb: 'watch', name: 'Gone with the Wind', durationSeconds: 13260 }, // 221 minutes
    { verb: 'watch', name: 'Lawrence of Arabia', durationSeconds: 12960 }, // 216 minutes
    { verb: 'watch', name: 'The Irishman', durationSeconds: 12600 }, // 210 minutes
    { verb: 'watch', name: 'Avengers: Endgame', durationSeconds: 10800 }, // 180 minutes
    { verb: 'watch', name: 'Titanic', durationSeconds: 11640 }, // 194 minutes
    { verb: 'watch', name: 'The Wolf of Wall Street', durationSeconds: 10800 }, // 180 minutes
]

// Specific podcasts with episode lengths (in seconds)
const PODCASTS: Activity[] = [
    { verb: 'listen to', name: 'The Joe Rogan Experience (episode)', durationSeconds: 10800 }, // 3 hours
    { verb: 'listen to', name: 'This American Life (episode)', durationSeconds: 3600 }, // 1 hour
    { verb: 'listen to', name: 'Radiolab (episode)', durationSeconds: 3600 }, // 1 hour
    { verb: 'listen to', name: 'Serial (episode)', durationSeconds: 3600 }, // 1 hour
    { verb: 'listen to', name: 'The Daily (episode)', durationSeconds: 1800 }, // 30 minutes
    { verb: 'listen to', name: 'Stuff You Should Know (episode)', durationSeconds: 3600 }, // 1 hour
    { verb: 'listen to', name: 'Freakonomics Radio (episode)', durationSeconds: 3600 }, // 1 hour
    { verb: 'listen to', name: 'Planet Money (episode)', durationSeconds: 1800 }, // 30 minutes
]

// Video games with playthrough times (in seconds)
const GAMES: Activity[] = [
    { verb: 'play', name: 'The Legend of Zelda: Breath of the Wild', durationSeconds: 180000 }, // 50 hours
    { verb: 'play', name: 'The Witcher 3: Wild Hunt', durationSeconds: 172800 }, // 48 hours
    { verb: 'play', name: 'Red Dead Redemption 2', durationSeconds: 180000 }, // 50 hours
    { verb: 'play', name: 'Elden Ring', durationSeconds: 144000 }, // 40 hours
    { verb: 'play', name: 'God of War (2018)', durationSeconds: 72000 }, // 20 hours
    { verb: 'play', name: 'Portal 2', durationSeconds: 21600 }, // 6 hours
    { verb: 'play', name: 'Celeste', durationSeconds: 18000 }, // 5 hours
    { verb: 'play', name: 'Stardew Valley (one season)', durationSeconds: 28800 }, // 8 hours
]

// Other activities
const OTHER_ACTIVITIES: Activity[] = [
    { verb: 'complete', name: 'a marathon', durationSeconds: 10800 }, // 3 hours
    { verb: 'take', name: 'a cross-country flight (US)', durationSeconds: 18000 }, // 5 hours
    { verb: 'cook', name: 'a Thanksgiving dinner', durationSeconds: 14400 }, // 4 hours
    { verb: 'have', name: 'a full night\'s sleep', durationSeconds: 28800 }, // 8 hours
    { verb: 'watch', name: 'a full season of a TV show (10 episodes)', durationSeconds: 21600 }, // 6 hours
    { verb: 'learn', name: 'a new language (intensive day)', durationSeconds: 28800 }, // 8 hours
    { verb: 'drive', name: 'from New York to Boston', durationSeconds: 10800 }, // 3 hours
    { verb: 'bake', name: 'sourdough bread (including proofing)', durationSeconds: 21600 }, // 6 hours
]

function calculateTimeRemaining(): TimeRemaining {
    const now = new Date()
    const diff = TARGET_DATE.getTime() - now.getTime()

    if (diff <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0, totalSeconds: 0 }
    }

    const totalSeconds = Math.floor(diff / 1000)
    const days = Math.floor(totalSeconds / (24 * 60 * 60))
    const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60))
    const minutes = Math.floor((totalSeconds % (60 * 60)) / 60)
    const seconds = totalSeconds % 60

    return { days, hours, minutes, seconds, totalSeconds }
}

function formatDuration(seconds: number): string {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)

    if (hours > 0 && minutes > 0) {
        return `${hours} ${hours === 1 ? 'hour' : 'hours'} ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`
    } else if (hours > 0) {
        return `${hours} ${hours === 1 ? 'hour' : 'hours'}`
    } else {
        return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`
    }
}

function shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array]
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    return shuffled
}

function getTimeExamples(totalSeconds: number, selectedActivities?: Activity[]): TimeExample[] {
    const allActivities = selectedActivities || [...BOOKS, ...FILMS, ...PODCASTS, ...GAMES, ...OTHER_ACTIVITIES]

    // Filter activities that fit within the time, and calculate how many times you could do each
    const validExamples: TimeExample[] = []

    for (const activity of allActivities) {
        if (activity.durationSeconds <= totalSeconds) {
            const count = Math.floor(totalSeconds / activity.durationSeconds)
            if (count >= 1) {
                validExamples.push({
                    verb: activity.verb,
                    name: activity.name,
                    count,
                    durationText: formatDuration(activity.durationSeconds)
                })
            }
        }
    }

    return validExamples
}

export function HolidayPage() {
    const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>(calculateTimeRemaining())

    // Randomize activities on mount only
    const selectedActivities = useMemo(() => {
        const allActivities = [...BOOKS, ...FILMS, ...PODCASTS, ...GAMES, ...OTHER_ACTIVITIES]
        return shuffleArray(allActivities).slice(0, 20) // Select 20 random activities to ensure variety
    }, [])

    // Update examples based on current time, using the pre-selected activities
    const examples = useMemo(() => {
        return getTimeExamples(timeRemaining.totalSeconds, selectedActivities).slice(0, 6)
    }, [timeRemaining.totalSeconds, selectedActivities])

    useEffect(() => {
        const interval = setInterval(() => {
            const newTime = calculateTimeRemaining()
            setTimeRemaining(newTime)
        }, 1000)

        return () => clearInterval(interval)
    }, [])

    const isExpired = timeRemaining.totalSeconds === 0

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <h1 className={styles.title}>Holiday Countdown</h1>
                <p className={styles.subtitle}>Time until April 4th, 2026</p>

                {isExpired ? (
                    <div className={styles.expired}>
                        <p className={styles.expiredText}>🎉 The holiday has arrived! 🎉</p>
                    </div>
                ) : (
                    <>
                        <div className={styles.countdown}>
                            <div className={styles.timeUnit}>
                                <div className={styles.timeValue}>{timeRemaining.days}</div>
                                <div className={styles.timeLabel}>Days</div>
                            </div>
                            <div className={styles.separator}>:</div>
                            <div className={styles.timeUnit}>
                                <div className={styles.timeValue}>{String(timeRemaining.hours).padStart(2, '0')}</div>
                                <div className={styles.timeLabel}>Hours</div>
                            </div>
                            <div className={styles.separator}>:</div>
                            <div className={styles.timeUnit}>
                                <div className={styles.timeValue}>{String(timeRemaining.minutes).padStart(2, '0')}</div>
                                <div className={styles.timeLabel}>Minutes</div>
                            </div>
                            <div className={styles.separator}>:</div>
                            <div className={styles.timeUnit}>
                                <div className={styles.timeValue}>{String(timeRemaining.seconds).padStart(2, '0')}</div>
                                <div className={styles.timeLabel}>Seconds</div>
                            </div>
                        </div>

                        {examples.length > 0 && (
                            <div className={styles.examples}>
                                <h2 className={styles.examplesTitle}>In this time, you could...</h2>
                                <div className={styles.examplesGrid}>
                                    {examples.map((example, index) => (
                                        <div key={index} className={styles.exampleCard}>
                                            <div className={styles.exampleLabel}>
                                                <span className={styles.exampleVerb}>{example.verb}</span>{' '}
                                                <span className={styles.exampleName}>{example.name}</span>
                                            </div>
                                            <div className={styles.exampleCount}>{example.count.toLocaleString()}</div>
                                            <div className={styles.exampleTimes}>times</div>
                                            <div className={styles.exampleDuration}>
                                                ({example.durationText} each)
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}

